import { FC, useCallback, useMemo } from "react";
import { Virtuoso } from "react-virtuoso";
import { useQueryNewNotifications } from "Hooks/notifications/useQueryNewNotifications";
import { useQueryTemporaryNotifications } from "Hooks/notifications/useQueryTemporaryNotifications";
import {
  filterItemAuxIdByActivityAction,
  filterItemIdByActivityAction,
  INotification,
  INotificationMultipleReadRequest,
  NotificationTypeEnum
} from "Data/interfaces/Notification/INotification";
import SpinCustom from "Components/UI/SpinCustom";
import Card from "./components/Card";
import Empty from "./components/Empty";
import LoadingWidgetNotifications from "Components/UI/CustomLoading/LoadingWidgetNotifications";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { ActivitiesActions } from "Store/Activities/Activities.actions";
import { useNotification } from "Context/NotificationContext";
import { Container, EmptyContainer } from "./styles";

interface IContent {
  constructionSiteId: number | null;
  personalKey: 'NOTIFICATIONS_PERSONAL_KEY' | null;
  readingNotificationId: string | null;
  isLoadingReadNotification: boolean;
  onRedirect: (url: string) => void;
  onMarkAsRead: (readRow: INotification) => void;
}

const Content: FC<IContent> = ({
  constructionSiteId,
  personalKey,
  readingNotificationId,
  isLoadingReadNotification,
  onRedirect,
  onMarkAsRead
}) => {
  const dispatch = useDispatch();
  const {
    data: notifications,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    fetchNextPage
  } = useQueryNewNotifications(constructionSiteId, personalKey);
  const {
    data: temporaryNotifications
  } = useQueryTemporaryNotifications(constructionSiteId, personalKey);
  const { addNotificationsToRead } = useNotification();

  const allNotifications = useMemo(() => {
    const data: INotification[] = [];
    const setMap = new Set<string>();
    for (const notification of temporaryNotifications) {
      if (notification?.NotificationId && !setMap.has(notification.NotificationId)) {
        setMap.add(notification.NotificationId);
        data.push(notification);
      }
    }
    for (const notification of notifications) {
      if (notification?.NotificationId && !setMap.has(notification.NotificationId)) {
        setMap.add(notification.NotificationId);
        data.push(notification);
      }
    }
    setMap.clear();
    data.sort((a, b) => dayjs(b.CreationDate).diff(dayjs(a.CreationDate)));
    return data;
  }, [notifications, temporaryNotifications]);

  const handleRedirect = useCallback((item: INotification) => {
    if (item?.ItemUrl) {
      if (item.Type !== NotificationTypeEnum.Activity) {
        const notificationAsView: INotificationMultipleReadRequest = {
          constructionSiteId: item.ConstructionSiteFk,
          notificationIds: [item.NotificationId, ...item?.AggNotificationIds || []],
        };
        addNotificationsToRead(
          item.ItemId,
          notificationAsView,
          item.Type === NotificationTypeEnum.Plotting ? undefined : item.ItemUrl,
        );
        return onRedirect(item.ItemUrl);
      }
      if (item.Type === NotificationTypeEnum.Activity) {
        let activityId: string | null = null;
        if (filterItemIdByActivityAction.includes(item.Action)) {
          activityId = item?.ItemId;
        }
        if (filterItemAuxIdByActivityAction.includes(item.Action)) {
          activityId = item?.ItemAuxId;
        }
        if (!activityId) return;

        dispatch(ActivitiesActions.clearListActivities());
        const notificationsById = allNotifications.filter(notification => {
          return notification?.ItemAuxId === activityId ||
            notification?.ItemId === activityId;
        })
        const notificationsReadData: Record<string, INotificationMultipleReadRequest> = {};
        for (const notification of notificationsById) {
          const readKeyId = `${notification.ConstructionSiteFk}-${notification.ItemAuxId}`;
          if (readKeyId in notificationsReadData) {
            notificationsReadData[readKeyId].notificationIds.push(notification.NotificationId);
          } else {
            notificationsReadData[readKeyId] = {
              constructionSiteId: notification.ConstructionSiteFk,
              notificationIds: [notification.NotificationId],
              itemAuxId: notification.ItemAuxId,
            };
          }

          if (
            notification?.AggNotificationIds &&
            notification.AggNotificationIds?.length > 0
          ) {
            for (const n of notification.AggNotificationIds) {
              const readKeyIdAgg = `${notification.ConstructionSiteFk}-${notification.ItemAuxId}`;
              if (readKeyIdAgg in notificationsReadData) {
                notificationsReadData[readKeyIdAgg].notificationIds.push(n);
              } else {
                notificationsReadData[readKeyIdAgg] = {
                  constructionSiteId: notification.ConstructionSiteFk,
                  notificationIds: [n],
                  itemAuxId: notification.ItemAuxId,
                };
              }
            }
          }
        }
        dispatch(ActivitiesActions.readNotificationsData({
          activityId: activityId,
          readData: notificationsReadData,
        }));
        return onRedirect(item.ItemUrl);
      }
    }
  }, [allNotifications, dispatch, onRedirect, addNotificationsToRead]);

  return (
    <Container height={400}>
      {isLoading && (
        <LoadingWidgetNotifications multiple={5} />
      )}
      {!isLoading && (
        <Virtuoso
          totalCount={allNotifications?.length}
          data={allNotifications}
          initialTopMostItemIndex={0}
          itemContent={(index, item) => (
            <Card
              key={`new-notif-${index}-${item.NotificationId}`}
              item={item}
              isPersonal={!!personalKey}
              readingNotificationId={readingNotificationId}
              isLoadingReadNotification={isLoadingReadNotification}
              onRedirect={handleRedirect}
              onMarkAsRead={onMarkAsRead}
            />
          )}
          style={{
            flex: 1,
            width: '100%',
            height: 400,
          }}
          endReached={() => {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          components={{
            Footer: () => {
              if (isFetchingNextPage) {
                return (
                  <EmptyContainer>
                    <SpinCustom />
                  </EmptyContainer>
                );
              }
              return null;
            },
            EmptyPlaceholder: () => <Empty onRedirect={onRedirect} />,
          }}
        />
      )}
    </Container>
  );
};

export default Content;
